import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiEndpoint } from 'app/agriculture/queries/apiEndpoints';
import {
    Farm,
    farmSchema,
} from 'app/agriculture/contexts/RequiredStartupProviders/FarmContext/farmSchema';
import { startupQueries } from 'app/agriculture/contexts/RequiredStartupProviders/queries';
import { AxiosResponse } from 'axios';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';

export function useFarm() {
    const { agricultureToastService } = useContext(ServicesContext);
    const queryClient = useQueryClient();
    const currentFarm = useSelectedFarm();
    const instance = createAxiosInstance();

    const updateFarm = useMutation({
        mutationFn: async (params: Partial<Farm>) => {
            const { data } = await instance.patch<unknown, AxiosResponse<unknown>, Partial<Farm>>(
                `${ApiEndpoint.farms}${currentFarm.id}/`,
                params,
            );
            return farmSchema.parse(data);
        },
        onSuccess: (updatedFarm) => {
            // replace relevant farm in farms query cache with updated farm
            queryClient.setQueryData(startupQueries.farms.queryKey, (farms: Farm[] = []) => {
                return farms.map((farm) => {
                    return farm.id === updatedFarm.id ? updatedFarm : farm;
                });
            });

            // replace single farm query cache with updated farm
            queryClient.setQueryData([`${ApiEndpoint.farms}${updatedFarm.id}`], updatedFarm);
        },
        onError: async function (error) {
            console.error('Error updating farm', error);
            await agricultureToastService.showToast({
                title: 'Farm Update Failed',
                subtext: 'Something went wrong',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: startupQueries.farms.queryKey });
        },
    });

    return { farm: currentFarm, updateFarm };
}
